import  React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "src/util/cn"

const buttonVariants = cva(
  `inline-flex gap-1 items-center justify-center whitespace-nowrap rounded-button ring-offset-background transition-colors 
  focus-visible:outline-none transition-all
  disabled:pointer-events-none disabled:before:opacity-20 disabled:before:bg-gray-80 disabled:before:bg-none disabled:text-gray-80
  
  `,
  {
    variants: {
      variant: {
        default: "relative bg-inherit border hover:border-gray-60",
        accent:
          "relative bg-inherit default-button-accent hover:default-button-accent-hover active:default-button-accent-hover ",
        link: "underline-offset-4 underline underline-offset-2 text-gray-60 active:text-white hover:text-white text-xs lg:text-sm",
        input: "bg-gray-80 rounded-[46px] justify-start",
        "gradient-link":
          "gradient-text-green relative text-sm after:content-[''] after:absolute after:z-[-1] after:primary-gradient-green after:-bottom-0.5 after:w-full after:h-[1px] after:opacity-0 after:hover:opacity-100 after:transition-opacity",
        warning:
          "relative bg-inherit text-red default-button-warning hover:default-button-warning-hover active:default-button-warning-hover",
        primary:
          "mx-auto sm:mx-0 px-6 py-2 bg-[#D0D0D0] text-[#0B0B0B] font-popins text-[14px] rounded-full   hover:bg-[#FFFFFF] cursor-pointer disabled:bg-[#717171] disabled:cursor-not-allowed border border-solid border-transparent focus:border-[#5F42FF] focus:outline-none",
      },
      size: {
        default: "px-[15px] py-[12px] text-[14px]",
        sm: "px-[12px] py-1.5 lg:py-[7px] text-[13px] leading-[20px]",
        primary: "px-[15px] py-[12px] text-sm w-full lg:max-w-[320px]",
        none: "",
      },
      state: {
        default: "",
        "default-active": "default-button-hover !text-white",
        loading: "default-button-accent loading-ellipsis",
        disabled: "pointer-events-none opacity-50",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      state: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, state, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, state, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)

Button.displayName = "Button"

export { Button, buttonVariants }
